import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Classes for a thin native scrollbar whose thumb is transparent except while `isScrolling` (from
 * {@link useScrollActivity}). Unlike the floating scrollbar, it still takes layout width.
 */
export const hiddenUntilScrollClass = (isScrolling: boolean) =>
  isScrolling
    ? "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-foreground/50"
    : "scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent";

/**
 * Tracks whether an element is actively being scrolled, for scrollbars that should only show while
 * scrolling. Call `onScroll` from the scroll container's scroll handler; `isScrolling` turns on with
 * each call and off once `idleMs` pass without another, since no scroll-end event has usable support.
 *
 * @param [idleMs=1000] - How long scrolling must pause before `isScrolling` turns off
 *
 * @example
 * ```tsx
 * const { isScrolling, onScroll } = useScrollActivity();
 * return <div onScroll={onScroll} className={cn("overflow-y-auto", hiddenUntilScrollClass(isScrolling))} />;
 * ```
 */
export const useScrollActivity = (idleMs = 1000) => {
  const [isScrolling, setIsScrolling] = useState(false);
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onScroll = useCallback(() => {
    setIsScrolling(true);
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    idleTimeoutRef.current = setTimeout(() => setIsScrolling(false), idleMs);
  }, [idleMs]);

  useEffect(
    () => () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    },
    []
  );

  return { isScrolling, onScroll };
};
